import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as svc from '../../../../services/epcWorkLibraryService';
import type {
  CreateWorkLibraryInput,
  UpdateWorkLibraryInput,
  WorkLibraryItem,
  WorkLibraryKind,
} from '../../../../services/epcWorkLibraryService';

export type { WorkLibraryItem, WorkLibraryKind };

const ROOT = ['epc', 'work-library'] as const;
const listKey = (kind: WorkLibraryKind, disciplineKey: string | null, includeInactive: boolean) =>
  [...ROOT, kind, disciplineKey ?? '__common__', includeInactive] as const;

export function useWorkLibrary(
  kind: WorkLibraryKind,
  disciplineKey: string | null,
  opts: { includeInactive?: boolean; enabled?: boolean } = {}
) {
  const includeInactive = opts.includeInactive ?? false;
  return useQuery<WorkLibraryItem[]>({
    queryKey: listKey(kind, disciplineKey, includeInactive),
    enabled: opts.enabled ?? true,
    queryFn: () => svc.listWorkLibrary({ kind, disciplineKey, includeInactive }),
    staleTime: 60_000,
  });
}

function useInvalidateLibrary() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ROOT });
}

export function useCreateLibraryItem() {
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: (input: CreateWorkLibraryInput) => svc.createWorkLibraryItem(input),
    onSuccess: invalidate,
  });
}

export function useUpdateLibraryItem() {
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWorkLibraryInput }) =>
      svc.updateWorkLibraryItem(id, input),
    onSuccess: invalidate,
  });
}

export function useDeactivateLibraryItem() {
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: (id: string) => svc.deactivateWorkLibraryItem(id),
    onSuccess: invalidate,
  });
}

/**
 * Alias for useCreateLibraryItem — used by GateAuthoring's "Save as template" action.
 * Keep the name so GateAuthoring's import doesn't need to change.
 */
export const useSaveWorkLibraryItem = useCreateLibraryItem;
